import { guardPreview } from '@/lib/previewMode'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { portalApi, type PortalClientProfileDto } from '@/api/portal'
import { Building2, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { addressLabels } from '@/lib/address'
import { usePortalDates } from '@/hooks/useDates'
import { usePortalPermissions } from '@/hooks/usePortalPermissions'

type FormData = Omit<PortalClientProfileDto, 'name'>

function Field({ id, label, children }: { id?: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  )
}

export default function ProfilePage() {
  const { region } = usePortalDates()
  const permissions = usePortalPermissions()
  const labels = addressLabels(region)
  const queryClient = useQueryClient()
  const [saved, setSaved] = useState(false)

  const { data: profile, isLoading } = useQuery({
    queryKey: ['portal-profile'],
    queryFn: () => portalApi.getProfile().then(r => r.data),
  })

  const [form, setForm] = useState<FormData | null>(null)

  if (profile && !form) {
    setForm({
      contactName:  profile.contactName,
      contactEmail: profile.contactEmail,
      phone:        profile.phone,
      addressLine1: profile.addressLine1,
      addressLine2: profile.addressLine2,
      city:         profile.city,
      county:       profile.county,
      postcode:     profile.postcode,
      country:      profile.country,
    })
  }

  const mutation = useMutation({
    mutationFn: () => portalApi.updateProfile(form!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-profile'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const set = (field: keyof FormData, value: string) =>
    setForm(f => f ? { ...f, [field]: value || null } : f)

  if (isLoading || !form) return <p className="text-gray-500">Loading…</p>

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 size={22} className="text-[var(--brand-primary)]" />
          Organisation details
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Your contact details as seen by {profile?.name ? <strong>{profile.name}</strong> : 'your training provider'}.
        </p>
      </div>

      <form onSubmit={e => { e.preventDefault(); if (guardPreview()) return; mutation.mutate() }} className="space-y-6">
        {!permissions.editDetails && (
          <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            These details are read-only. Contact your training provider to change them.
          </p>
        )}
        <fieldset disabled={!permissions.editDetails} className="space-y-6 min-w-0 border-0 p-0 m-0">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Contact details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field id="contactName" label="Contact name">
                <Input
                  id="contactName"
                  value={form.contactName ?? ''}
                  onChange={e => set('contactName', e.target.value)}
                  placeholder="e.g. Jane Smith"
                />
              </Field>
              <Field id="contactEmail" label="Contact email">
                <Input
                  id="contactEmail"
                  type="email"
                  value={form.contactEmail}
                  onChange={e => set('contactEmail', e.target.value)}
                  required
                />
              </Field>
            </div>
            <Field id="phone" label="Phone">
              <Input
                id="phone"
                type="tel"
                value={form.phone ?? ''}
                onChange={e => set('phone', e.target.value)}
                placeholder="e.g. 01234 567890"
              />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field id="addressLine1" label="Address line 1">
              <Input
                id="addressLine1"
                value={form.addressLine1 ?? ''}
                onChange={e => set('addressLine1', e.target.value)}
              />
            </Field>
            <Field id="addressLine2" label="Address line 2">
              <Input
                id="addressLine2"
                value={form.addressLine2 ?? ''}
                onChange={e => set('addressLine2', e.target.value)}
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field id="city" label="City">
                <Input
                  id="city"
                  value={form.city ?? ''}
                  onChange={e => set('city', e.target.value)}
                />
              </Field>
              <Field id="county" label={labels.county}>
                <Input
                  id="county"
                  value={form.county ?? ''}
                  onChange={e => set('county', e.target.value)}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field id="postcode" label={labels.postcode}>
                <Input
                  id="postcode"
                  value={form.postcode ?? ''}
                  onChange={e => set('postcode', e.target.value)}
                />
              </Field>
              <Field id="country" label="Country">
                <Input
                  id="country"
                  value={form.country ?? ''}
                  onChange={e => set('country', e.target.value)}
                />
              </Field>
            </div>
          </CardContent>
        </Card>

        </fieldset>

        {permissions.editDetails && (
          <div className="flex items-center justify-end gap-3">
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                <Check size={15} /> Saved
              </span>
            )}
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}
